import {
    useEffect,
    useState,
} from 'react';

import {DnsZone} from '../types';

// FNP-1191 CRUD half — SELECTION, module-scoped for the same reason the keyword
// and the filters are (FNP-815).
//
// The DELETE pattern doc lifts selection into the IndexPage and prop-drills it
// into both the header and the table. That works when the two are siblings in
// one file — and this module's header is rendered by `SimplePageLayout` as a
// `pageHeader` prop while the table is rendered as its child, so they are NOT
// siblings that can share a useState without threading it through the layout
// component in between.
//
// The doc's SUBSTANCE — one selection, visible to both the toolbar and the
// table — is kept exactly. Only the mechanism differs, and it matches the two
// stores this module already has, so a reader finds three stores built the same
// way rather than two stores and one prop-drill.
//
// ⚠️ SELECTION IS KEYED ON THE ZONE OBJECT, and a zone is identified by BOTH
// its id and its connectionId. `MantineDataTable` compares by `idAccessor`
// (`id`), which is enough within one page — but the delete call needs the
// connectionId too, so the full records are kept rather than a set of ids.

let globalSelection: DnsZone[] = [];
const selectionListeners: Set<(selected: DnsZone[]) => void> = new Set();

const notifySelectionChange = (selected: DnsZone[]) => {
    globalSelection = selected;
    selectionListeners.forEach(listener => listener(selected));
};

// Test-only reset. The store outlives a component tree by design, which means
// it also outlives a test case — without this, a selection made in one test
// leaks into the next.
export const resetZonesSelection = () => {
    globalSelection = [];
    selectionListeners.clear();
};

export const useZonesSelection = () => {

    const [selectedRecords, setSelected] = useState<DnsZone[]>(globalSelection);

    useEffect(() => {
        const listener = (selected: DnsZone[]) => {
            setSelected(selected);
        };
        selectionListeners.add(listener);
        // A consumer mounting after a selection was made must not start empty.
        setSelected(globalSelection);
        return () => {
            selectionListeners.delete(listener);
        };
    }, []);

    return {
        selectedRecords,
        setSelectedRecords: (selected: DnsZone[]) => notifySelectionChange(selected),
        // The doc's "clear selection after successful deletion" step.
        clearSelection: () => notifySelectionChange([]),
    };
};
